/**
 * Client for the leaderboard read API.
 *
 * Every call goes through `request()` so the bearer token and the 401 handling
 * live in exactly one place.
 */
import type {
  BoardEntryDetail,
  BoardPage,
  BoardSortColumn,
  BoardTierFloor,
  CancelResult,
  CompoundOrder,
  CrossRunReviewLogPage,
  DeadEndPage,
  DeleteResult,
  KgContradictionPage,
  LatestBoard,
  ReportPage,
  ReportRequestResult,
  RetireResult,
  RetireRunResult,
  ReviewLogPage,
  RunDetail,
  RunSummary,
  SortColumn,
  SortDirection,
  TargetPage,
} from './types'
import { DEFAULT_PAGE_SIZE } from './types'
import { saveBlob } from '../lib/download'

/**
 * Empty in dev, where vite.config.ts proxies /api to localhost:8001. In
 * production this is the ALB origin and calls are cross-origin, which is why
 * api/main.py's CORS_ORIGINS must name the CloudFront domain.
 */
const BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

/** Thrown for any non-2xx. `status` is what the UI branches on for 401. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export type TokenGetter = () => Promise<string | null>

async function request<T>(
  path: string,
  getToken: TokenGetter,
  signal?: AbortSignal,
  method: 'GET' | 'POST' | 'DELETE' = 'GET',
  // A JSON body, for the two routes that carry free prose. It goes in a body and
  // not a query string because a retirement justification is a sentence a person
  // typed, and a URL lands in every access log and proxy cache on the path.
  body?: unknown,
): Promise<T> {
  const token = await getToken()
  const resp = await fetch(`${BASE}/api${path}`, {
    method,
    signal,
    headers: {
      Accept: 'application/json',
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!resp.ok) {
    // FastAPI puts the message in `detail`; a 502 from the ALB is HTML and has
    // no JSON body at all, so parsing must not be allowed to throw over the
    // real error.
    let detail = resp.statusText
    try {
      const body = await resp.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch {
      /* non-JSON body — keep the status text */
    }
    throw new ApiError(resp.status, detail)
  }
  return (await resp.json()) as T
}

/**
 * The landing view: the newest run that ranked anything, with its first page.
 *
 * `limit` is a page, not the whole run — `total` on the response is the real
 * count, and later pages come from `listTargets`, which takes an `offset` this
 * endpoint does not.
 */
export function getLatest(
  getToken: TokenGetter,
  signal?: AbortSignal,
  limit: number = DEFAULT_PAGE_SIZE,
): Promise<LatestBoard> {
  return request<LatestBoard>(
    `/leaderboard/latest?limit=${limit}`,
    getToken,
    signal,
  )
}

export function listRuns(
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<RunSummary[]> {
  return request<RunSummary[]>('/leaderboard/runs?limit=50', getToken, signal)
}

export function getRun(
  runId: string,
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<RunDetail> {
  return request<RunDetail>(
    `/leaderboard/runs/${encodeURIComponent(runId)}`,
    getToken,
    signal,
  )
}

/**
 * Ask a live sweep to stop.
 *
 * The only mutating call in this client. It does not stop the run
 * synchronously — the sweep runs in the AgentCore runtime, not behind this ALB,
 * and notices the flag on its next poll (~10 s). So callers must refresh rather
 * than assume the returned `status` is final.
 *
 * No `signal`: aborting the fetch would not un-request the cancellation, and a
 * component unmounting mid-flight must not leave the caller thinking the click
 * was discarded.
 */
export function cancelRun(
  runId: string,
  getToken: TokenGetter,
): Promise<CancelResult> {
  return request<CancelResult>(
    `/leaderboard/runs/${encodeURIComponent(runId)}/cancel`,
    getToken,
    undefined,
    'POST',
  )
}

/**
 * Delete a finished run that produced no ranking.
 *
 * The API refuses anything holding ranked targets with a 409, so a run that
 * found something cannot be removed from here however the caller asks. Callers
 * must surface that message rather than treat the failure as a transport error:
 * it is the answer, not a glitch.
 *
 * No `signal`, for the same reason as `cancelRun`: the delete is not undone by
 * aborting the fetch.
 */
export function deleteRun(
  runId: string,
  getToken: TokenGetter,
): Promise<DeleteResult> {
  return request<DeleteResult>(
    `/leaderboard/runs/${encodeURIComponent(runId)}`,
    getToken,
    undefined,
    'DELETE',
  )
}

/**
 * Take one board row off the default board, with the reason the user typed.
 *
 * Not a delete: the row and its justification stay, so a later sweep proposing the
 * same target cannot read as a first discovery. Nothing scientific moves — no score,
 * tier, Elo or rank — and the per-run rows behind it are untouched.
 *
 * `justification` is required by the API (422 on an empty one), so the caller must
 * disable its confirm button rather than send a blank. Six months from now this
 * sentence is the only thing distinguishing a discarded harness test from a
 * hypothesis a scientist rejected on the evidence.
 *
 * `encodeURIComponent` on the key, which contains a pipe and may contain slashes.
 * The route is declared `:path`, so the server accepts a raw slash too, but encoding
 * is what stops the pipe and any `#` reaching the wire unescaped.
 *
 * No `signal`, for the same reason as `cancelRun`: aborting the fetch does not
 * un-retire the row.
 */
export function retireBoardTarget(
  targetKey: string,
  justification: string,
  getToken: TokenGetter,
): Promise<RetireResult> {
  return request<RetireResult>(
    `/leaderboard/board/${encodeURIComponent(targetKey)}/retire`,
    getToken,
    undefined,
    'POST',
    { justification },
  )
}

/**
 * Retire every board row one run contributed, with one justification.
 *
 * The granularity a testing phase needs: one bad sweep put 29 rows on the board and
 * retiring them individually is 29 typed reasons for one decision.
 *
 * `retired: 0` is a success, not a failure — a run that never reached a writer, or
 * one whose rows were already retired. Render the count; do not treat zero as an
 * error, because the API deliberately does not 404 on it.
 */
export function retireRun(
  runId: string,
  justification: string,
  getToken: TokenGetter,
): Promise<RetireRunResult> {
  return request<RetireRunResult>(
    `/leaderboard/runs/${encodeURIComponent(runId)}/retire`,
    getToken,
    undefined,
    'POST',
    { justification },
  )
}

/** The cumulative board — every run's conclusions, one row per target. */
export function listBoard(
  opts: {
    sort: BoardSortColumn
    direction: SortDirection
    minRuns?: number
    /** A tier floor, or null for the whole board. */
    tier?: BoardTierFloor | null
    limit?: number
    offset?: number
  },
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<BoardPage> {
  const params = new URLSearchParams({
    sort: opts.sort,
    direction: opts.direction,
    min_runs: String(opts.minRuns ?? 1),
    limit: String(opts.limit ?? DEFAULT_PAGE_SIZE),
    offset: String(opts.offset ?? 0),
  })
  // Omitted rather than sent empty: the API's default is "no floor", and `tier=`
  // would be a string it has to reject to avoid filtering the board to nothing.
  if (opts.tier) params.set('tier', opts.tier)
  return request<BoardPage>(`/leaderboard/board?${params}`, getToken, signal)
}

/**
 * One board entry plus every run's own observation of it.
 *
 * `encodeURIComponent`, not a raw interpolation: a target key is
 * `protein|mechanism` and the mechanism half is free text, so it can hold a
 * slash — which would otherwise be read as another path segment and 404.
 */
export function getBoardEntry(
  targetKey: string,
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<BoardEntryDetail> {
  return request<BoardEntryDetail>(
    `/leaderboard/board/${encodeURIComponent(targetKey)}`,
    getToken,
    signal,
  )
}

export function listTargets(
  runId: string,
  opts: {
    sort: SortColumn
    direction: SortDirection
    limit?: number
    offset?: number
  },
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<TargetPage> {
  const params = new URLSearchParams({
    sort: opts.sort,
    direction: opts.direction,
    limit: String(opts.limit ?? DEFAULT_PAGE_SIZE),
    offset: String(opts.offset ?? 0),
  })
  return request<TargetPage>(
    `/leaderboard/runs/${encodeURIComponent(runId)}/targets?${params}`,
    getToken,
    signal,
  )
}

/**
 * What review ruled out — the other half of a run.
 *
 * Ordering is fixed server-side (disqualified first, then by iteration): this is
 * a reason list, not a ranking, so there is no `sort`. An empty `dead_ends` with
 * `total: 0` means *nothing was disqualified*, which callers must not render the
 * same way as "not recorded".
 */
export function listDeadEnds(
  runId: string,
  opts: { limit?: number; offset?: number },
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<DeadEndPage> {
  const params = new URLSearchParams({
    limit: String(opts.limit ?? DEFAULT_PAGE_SIZE),
    offset: String(opts.offset ?? 0),
  })
  return request<DeadEndPage>(
    `/leaderboard/runs/${encodeURIComponent(runId)}/dead-ends?${params}`,
    getToken,
    signal,
  )
}

/**
 * Every verdict this run recorded, passes included — what `listDeadEnds` omits.
 *
 * The dead-end archive holds only what was ruled out, so a pass appeared nowhere and
 * "reviewed and cleared" was indistinguishable from "never reviewed". Ordering is
 * fixed server-side (review order), so there is no `sort`.
 *
 * `counts` covers the whole run, not this page — do not recount `entries` instead,
 * for the reason `DeadEndPage.counts` states.
 */
export function listReviewLog(
  runId: string,
  opts: { limit?: number; offset?: number },
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<ReviewLogPage> {
  const params = new URLSearchParams({
    limit: String(opts.limit ?? DEFAULT_PAGE_SIZE),
    offset: String(opts.offset ?? 0),
  })
  return request<ReviewLogPage>(
    `/leaderboard/runs/${encodeURIComponent(runId)}/review-log?${params}`,
    getToken,
    signal,
  )
}

/**
 * The same ledger across every run, newest first — the manual recheck of what was
 * disqualified.
 *
 * `verdict: 'failed'` is the read the recap mode is built around. `total` is the
 * count *under the filter*, and it is the only thing separating an exhausted list
 * from a capped page: a caller that counts the returned rows is claiming "this is
 * every failed review there has been", which is the one statement this cannot
 * support.
 */
export function listReviewLogAcrossRuns(
  opts: { verdict?: string; since?: string; limit?: number; offset?: number },
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<CrossRunReviewLogPage> {
  const params = new URLSearchParams({
    limit: String(opts.limit ?? DEFAULT_PAGE_SIZE),
    offset: String(opts.offset ?? 0),
  })
  // Only set when asked for. The server's filter is `if verdict:`, so an empty
  // string means "every verdict" there too — but it also echoes the parameter back
  // in `verdict`, and a caller rendering "showing: (blank)" over an unfiltered page
  // is describing a filter that was not applied.
  if (opts.verdict) params.set('verdict', opts.verdict)
  if (opts.since) params.set('since', opts.since)
  return request<CrossRunReviewLogPage>(
    `/leaderboard/review-log?${params}`,
    getToken,
    signal,
  )
}

/**
 * Where this run disputed the knowledge graph, with the source it cited.
 *
 * Every row carries at least one named external source — the filing tool refuses
 * one without. They are candidate corrections for the ontology repository:
 * nothing here wrote to the graph, and the disputed value is what this run
 * scored on, so a row never explains a target's rank. `total: 0` means the run
 * disputed nothing, which is the normal outcome and not "not recorded".
 */
export function listKgContradictions(
  runId: string,
  opts: { limit?: number; offset?: number },
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<KgContradictionPage> {
  const params = new URLSearchParams({
    limit: String(opts.limit ?? DEFAULT_PAGE_SIZE),
    offset: String(opts.offset ?? 0),
  })
  return request<KgContradictionPage>(
    `/leaderboard/runs/${encodeURIComponent(runId)}/kg-contradictions?${params}`,
    getToken,
    signal,
  )
}

/**
 * Order catalog hits from a board row through Simmate. The hits are named by
 * `(source, catalog_id)` only: the server rebuilds the form from the board row, and
 * the confirmation goes to the token's address, never one sent from here.
 */
export function requestOrder(
  targetKey: string,
  compounds: { source: string; catalog_id: string }[],
  note: string,
  getToken: TokenGetter,
): Promise<{ created: boolean; order: { status: string } }> {
  return request(
    `/leaderboard/board/${encodeURIComponent(targetKey)}/orders`,
    getToken,
    undefined,
    'POST',
    { compounds, note },
  )
}

export function listOrders(
  targetKey: string,
  getToken: TokenGetter,
): Promise<{ orders: CompoundOrder[] }> {
  return request(`/leaderboard/board/${encodeURIComponent(targetKey)}/orders`, getToken)
}

/**
 * Queue a full assessment for one ranked target, emailed to the signed-in user.
 *
 * No recipient parameter, and there is no way to add one: the server reads the
 * address from the verified token and ignores anything the client says. A body
 * here would be an open relay for unpublished target assessments.
 *
 * No `signal`, for the same reason `cancelRun` takes none — this starts work on
 * the server, and aborting the fetch would abandon a report that is already
 * being written rather than stop it.
 */
export function requestReport(
  runId: string,
  targetId: number,
  getToken: TokenGetter,
): Promise<ReportRequestResult> {
  return request<ReportRequestResult>(
    `/leaderboard/runs/${encodeURIComponent(runId)}/targets/${targetId}/report`,
    getToken,
    undefined,
    'POST',
  )
}

/**
 * Every report requested for this run, newest first.
 *
 * One call per run, not per target: the button state is needed for every visible
 * row and a page of 100 targets would otherwise be 100 round trips.
 */
export function listReports(
  runId: string,
  getToken: TokenGetter,
  signal?: AbortSignal,
): Promise<ReportPage> {
  return request<ReportPage>(
    `/leaderboard/runs/${encodeURIComponent(runId)}/reports`,
    getToken,
    signal,
  )
}

/**
 * Pull a finished report out of S3 through the authenticated route and hand it
 * to the browser as a file.
 *
 * Not an `<a href>`: the route is behind Entra and a plain navigation carries no
 * `Authorization` header, so the link would 401. And deliberately not a presigned
 * URL — that is a bearer capability in a query string that bypasses Entra
 * entirely, and these documents carry confidential internal protein ids.
 *
 * Consequence to keep in mind: the whole PDF lands in memory as a blob before it
 * is saved. Reports are a few hundred KB, so that is fine here and would not be
 * for something large.
 */
export async function downloadReport(
  reportId: number,
  getToken: TokenGetter,
): Promise<void> {
  const token = await getToken()
  const resp = await fetch(`${BASE}/api/leaderboard/reports/${reportId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!resp.ok) {
    let detail = resp.statusText
    try {
      const body = await resp.json()
      if (typeof body?.detail === 'string') detail = body.detail
    } catch {
      /* non-JSON body — keep the status text */
    }
    throw new ApiError(resp.status, detail)
  }

  // The server names the file (protein + report id + extension); fall back to
  // the id alone rather than to something that hides which report this is.
  const disposition = resp.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^";]+)"?/.exec(disposition)
  const filename = match?.[1] ?? `report-${reportId}.pdf`

  // Through `saveBlob` rather than inline, so the CSV exports and this share one
  // implementation of the part that is not obvious — the object URL is revoked on a
  // timeout because Safari reads the blob after the click handler returns.
  saveBlob(await resp.blob(), filename)
}
